// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {Component, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {NotificationService} from 'src/services/notification-service';
import {HeaderService} from 'src/app/header.service';
import {UserService} from 'src/services/user-service';
import {IManageUser, IUser, LoginStatus, UserRole, UserSource} from 'src/types/user';
import {ActivatedRoute, Router} from '@angular/router';
import {AlertDialogComponent} from 'src/app/dialogs/alert/alert.component';
import {Countries} from 'src/types/countries';
import multiDownload from 'multi-download';
import {UserSettingsComponent} from './user-settings/user-settings.component';
import authHelper from 'src/app/auth/authHelper';
import {AppComponent} from 'src/app/app.component';
import {D3eService} from 'src/services/d3e-service';
import {isD3EFreeUser} from 'src/shared/permissions-helper';
import {EncryptionService} from '../../../services/encryption.service';
import {GoogleAuthDialogComponent} from './twofactor.component';
import {CustomValidators} from 'src/app/shared/validators/custom-validators';
import {AuthService} from '../../../services/auth-service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ConfigurationService} from '../../../services/configuration-service';
import {
  IPasswordAlertDialogConfig,
  IPasswordAlertDialogResult,
  PasswordAlertDialogComponentComponent
} from '../../dialogs/password-alert-dialog-component/password-alert-dialog-component.component';
import {Observable} from 'rxjs';
import {Store} from '@ngrx/store';
import {loadUser} from '../../store/user/user.actions';
import {tap} from 'rxjs/operators';
import {AppState} from '../../store/app.state';
import {selectUser} from '../../store/user/user.selectors';
import { DeactivatableComponent } from '../../guards/page.guard';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false
})
export class ProfileComponent implements OnInit, DeactivatableComponent {
  registerForm: UntypedFormGroup;
  forcePassword = false;
  forceTwoFactor = false;
  isOrgAdmin = false;
  submittedProfile = false;
  selectedTab = 0;
  route: ActivatedRoute;
  countries = Countries;
  loadingMessage = 'loading';
  verifyCodeFormGroup: UntypedFormGroup;
  loading = false;
  myClock: number;
  receive_alerts: boolean;
  newRecoveryCode: string;
  generatedNewRecoveryCode = false;
  canEmailUser = false;
  emailFromToken: string;
  isD3EFreeUser = true;
  profileDataHasLoaded = false;
  showProfileCard = true;
  isCigentUser: boolean;
  showPaymentMethods: boolean;
  user$: Observable<IManageUser> = this.store.select(selectUser())
    .pipe(tap(() => this.generatedNewRecoveryCode = false));

  @ViewChild(UserSettingsComponent) userSettingsComponent: UserSettingsComponent;

  countryCodeStyles = {
    'font-size': '0.9em',
    'padding': '10px 5px',
    'width': '300px',
  };

  // DIALOG STRINGS //
  fEmailUpdated: string;
  fEmailSending: string;
  fEmailSent: string;
  fTooManyRequests: string;

  // PROFILE FIELDS //
  fFirstName: string;
  fLastName: string;
  fEmail: string;
  fEmailValid: string;
  fEmailReq: string;
  fEmailExists: string;
  fZip: string;
  fCountry: string;
  pTitleText: string;
  pTitleDesc: string;
  pSubmitButton: string;
  pDeleteKeysButton: string;

  // PASSWORD FIELDS //
  pwTitleText: string;
  pwTitleDescA: string;
  pwTitleDescB: string;

  // 2FA FIELDS //
  tfTitleText: string;
  tfDescText: string;
  tfInfo: string;
  tfSettings: string;
  tfStatusText: string;
  tfEnabled: string;
  tfDisabled: string;
  tfRecoveryButton: string;
  tfRecovery: string;
  tfActivate: string;
  tfDeactivate: string;
  tfNewRecovery: string;
  tfNo: string;
  tfYes: string;
  tfGenerateTitle: string;
  tfGenerateContent: string;
  tfDisableTitle: string;
  tfDisableContent: string;
  fErrorEmailReset: string;

  // EMAIL VERIFICATION FIELDS //
  evTitleText: string;
  evTitleDesc: string;
  evSentText: string;
  evResendText: string;
  evCodeText: string;
  evCodePlaceholder: string;
  evErrorText: string;
  evInvalidText: string;
  evSubmitText: string;
  evWaitText: string;

  // TAB HEADERS //
  profileTab: string;
  passwordTab: string;
  twoFactorTab: string;
  notificationsTab: string;
  paymentTab: string;

  title: string;

  constructor(
    private headerService: HeaderService,
    public dialog: MatDialog,
    private userSvc: UserService,
    private d3eService: D3eService,
    private encryptionService: EncryptionService,
    private formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
    route: ActivatedRoute,
    private notification: NotificationService,
    private router: Router,
    public appComponent: AppComponent,
    private authService: AuthService,
    private configure: ConfigurationService,
    private readonly store: Store<AppState>,
  ) {
    this.translate.get(['FIRST', 'LAST', 'PROFILE_EMAIL', 'PROFILE_ZIP', 'COUNTRY', 'EMAIL_REQ', 'EMAIL_VALID',
        'PROFILE_TITLE', 'PROFILE_DESC', 'PROFILE_TAB', 'TFA_TAB', 'PASSWORD_TAB', 'NOTIFICATIONS_TAB', 'PAYMENT_TAB',
        'PW_TITLE', 'PW_DESCA', 'PW_DESCB', 'TFA_TITLE', 'SUBMIT', 'DELETE_KEYS', 'TFA_DESC', 'TFA_INFO', 'TFA_SETTINGS', 'TFA_STATUS_TEXT',
        'TFA_ENABLED', 'TFA_DISABLED', 'TFA_RECOVERY_BUTTON', 'TFA_RECOVERY', 'TFA_ACTIVATE', 'TFA_DEACTIVATE',
        'TFA_NEW_RECOVERY', 'EV_HEADER', 'EV_DESC', 'EV_SENT', 'EV_RESEND', 'EV_CODE', 'EV_ENTER', 'EV_ERROR', 'EV_INVALID',
        'EV_SUBMIT', 'EV_WAIT', 'TITLE_MANAGE_PROFILE', 'EMAIL_EXISTS', 'NO', 'YES', 'TFA_GENERATE_TITLE',
        'TFA_GENERATE_CONTENT', 'TFA_DISABLE_TITLE', 'TFA_DISABLE_CONTENT', 'EMAIL_UPDATED', 'EMAIL_SENDING', 'EMAIL_SENT',
        'PROFILE_TOO_MANY_REQUESTS', 'ERROR_EMAIL_RESET'])
        .subscribe(vals => {
          this.fEmailUpdated = vals['EMAIL_UPDATED'];
          this.fEmailSending = vals['EMAIL_SENDING'];
          this.fEmailSent = vals['EMAIL_SENT'];
          this.fTooManyRequests = vals['PROFILE_TOO_MANY_REQUESTS'];

          this.fFirstName = vals['FIRST'];
          this.fLastName = vals['LAST'];
          this.fEmail = vals['PROFILE_EMAIL'];
          this.fZip = vals['PROFILE_ZIP'];
          this.fCountry = vals['COUNTRY'];

          this.fEmailValid =  vals['EMAIL_VALID'];
          this.fEmailReq = vals['EMAIL_REQ'];
          this.fEmailExists = vals['EMAIL_EXISTS'];

          this.pTitleText = vals['PROFILE_TITLE'];
          this.pTitleDesc = vals['PROFILE_DESC'];

          this.pSubmitButton = vals['SUBMIT'];
          this.pDeleteKeysButton = vals['DELETE_KEYS'];

          this.profileTab = vals['PROFILE_TAB'];
          this.passwordTab = vals['PASSWORD_TAB'];
          this.twoFactorTab = vals['TFA_TAB'];
          this.notificationsTab = vals['NOTIFICATIONS_TAB'];
          this.paymentTab = vals['PAYMENT_TAB'];

          this.pwTitleText = vals['PW_TITLE'];
          this.pwTitleDescA = vals['PW_DESCA'];
          this.pwTitleDescB = vals['PW_DESCB'];

          this.tfTitleText = vals['TFA_TITLE'];
          this.tfDescText = vals['TFA_DESC'];
          this.tfInfo = vals['TFA_INFO'];
          this.tfSettings = vals['TFA_SETTINGS'];
          this.tfStatusText = vals['TFA_STATUS_TEXT'];
          this.tfEnabled = vals['TFA_ENABLED'];
          this.tfDisabled = vals['TFA_DISABLED'];
          this.tfRecoveryButton = vals['TFA_RECOVERY_BUTTON'];
          this.tfRecovery = vals['TFA_RECOVERY'];
          this.tfActivate = vals['TFA_ACTIVATE'];
          this.tfDeactivate = vals['TFA_DEACTIVATE'];
          this.tfNewRecovery = vals['TFA_NEW_RECOVERY'];
          this.tfNo = vals['NO'];
          this.tfYes = vals['YES'];
          this.tfGenerateTitle = vals['TFA_GENERATE_TITLE'];
          this.tfGenerateContent = vals['TFA_GENERATE_CONTENT'];
          this.tfDisableTitle = vals['TFA_DISABLE_TITLE'];
          this.tfDisableContent = vals['TFA_DISABLE_CONTENT'];

          this.evTitleText = vals['EV_HEADER'];
          this.evTitleDesc = vals['EV_DESC'];
          this.evSentText = vals['EV_SENT'];
          this.evResendText = vals['EV_RESEND'];
          this.evCodeText = vals['EV_CODE'];
          this.evCodePlaceholder = vals['EV_ENTER'];
          this.evErrorText = vals['EV_ERROR'];
          this.evInvalidText = vals['EV_INVALID'];
          this.evSubmitText = vals['EV_SUBMIT'];
          this.evWaitText = vals['EV_WAIT'];

          this.title = vals['TITLE_MANAGE_PROFILE'];
          this.fErrorEmailReset = vals['ERROR_EMAIL_RESET'];
    });

    this.headerService.setTitle(this.title);
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-account');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(false);
    this.route = route;
    this.emailFromToken = authHelper.getUserFromToken();
    const signInSource = authHelper.getSourceFromToken();
    this.showPaymentMethods = this.configure.showPaymentMethods;
    this.isCigentUser = signInSource === UserSource.Cigent;

    this.registerForm = this.formBuilder.group({
      email: [{value: '', disabled: !this.isCigentUser}, [Validators.required,
        Validators.pattern(CustomValidators.emailPattern), CustomValidators.maxByteLength(255)]],
      fname: [{value: ''}, [Validators.nullValidator, Validators.maxLength(255)]],
      lname: [{value: ''}, [Validators.nullValidator, Validators.maxLength(255)]],
      zip: [{value: ''}, Validators.nullValidator],
      country: [{value: ''}, Validators.nullValidator],
      receiveAlerts: [{value: ''},  [Validators.nullValidator]],
      advanceFeatures: [{value: ''}, [Validators.nullValidator]],
    });

    this.registerForm.patchValue({
      email: this.loadingMessage,
      fname: this.loadingMessage,
      lname: this.loadingMessage,
      zip: this.loadingMessage
    });

    this.headerService.setNavMenuText('');

    this.verifyCodeFormGroup = this.formBuilder.group({
      verifyCode: ['', Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(6)])],
    });

    this.user$.subscribe(
      (user: IManageUser) => {
        if (user) {
          this.isD3EFreeUser = isD3EFreeUser(user);
          this.forcePassword = this.isCigentUser && user.force_password;
          this.forceTwoFactor = this.isCigentUser && user.force_two_factor;
          this.isOrgAdmin = user.role == UserRole.ADMIN;
          this.registerForm.controls['fname'].setValue(user.fname);
          this.registerForm.controls['lname'].setValue(user.lname);
          this.registerForm.controls['email'].setValue(user.email);
          this.registerForm.controls['zip'].setValue(user.billing_zip_code);
          this.registerForm.controls['country'].setValue(Countries.find(co => co.code === user.billing_country_code));
          this.receive_alerts = !!this.receive_alerts;
          this.profileDataHasLoaded = true;
        }
      },
      () => {}
    );
  }

  setTabFromParams() {
    this.route.paramMap.subscribe(params => {
      const tab = params.get('tab');
      switch (tab) {
        case 'contact':
          // this.headerService.setNavMenuText('Contact');
          this.selectedTab = 0;
          break;
        case 'password':
          // this.headerService.setNavMenuText('Password');
          this.selectedTab = 1;
          break;
        case '2fa':
          // this.headerService.setNavMenuText('TFA');
          this.selectedTab = 2;
          break;
        case 'settings':
          // this.headerService.setNavMenuText('TFA');
          this.selectedTab = 3;
          break;
          // case 'payment-methods':
          // this.selectedTab = 4;
          // break;
        default:
          // this.headerService.setNavMenuText('Contact');
          this.selectedTab = 0;
          break;
      }
    });
  }

  get f() {
    return this.registerForm.controls;
  }

  get e() {
    return this.verifyCodeFormGroup.controls;
   }

  ngOnInit(): void {
    this.setTabFromParams();
  }

  // Implementation of interface DeactivatableComponent in page.guard.ts
  getIsDirty(): boolean {
    return this.userSettingsComponent?.getIsDirty();
  }

  // Implementation of interface DeactivatableComponent in page.guard.ts
  canDeactivateResult(result: boolean): void {
    // no-op
  }

  // Implementation of interface DeactivatableComponent in page.guard.ts
  userValueChanged(): void {
    // no-op
  }

  onEditUser() {
    this.submittedProfile = true;

    if (this.registerForm.invalid) {
      return;
    }

    this.translate.get(['UPDATE_USER', 'UPDATE_COMPLETE', 'USER_UPDATE_FAIL']).subscribe(vals => {
      this.notification.showUpdate(vals['UPDATE_USER'] + '...');
      const userInfo: IUser = {
        email: this.registerForm.value.email,
        fname: this.registerForm.value.fname,
        lname: this.registerForm.value.lname,
        billing_zip_code: this.registerForm.value.zip,
        billing_country_code: this.registerForm.value.country.code,
        second_factor: this.registerForm.value.second_factor,
        receive_alerts: this.receive_alerts ? 1 : 0,
      };

      this.userSvc.updateUser(userInfo).subscribe(() => {
          this.userSvc.refreshUser();
          this.store.dispatch(loadUser());
          this.ngOnInit();
        },
        () => {
          this.notification.showError(vals['USER_UPDATE_FAIL']);
        },
        () => {
          this.notification.showSuccess(vals['UPDATE_COMPLETE']);
        }
      );
    });
  }

    onSubmit(user: IManageUser) {
        if (this.registerForm.invalid || this.registerForm.controls.email.errors) {
            this.registerForm.markAllAsTouched();
            return;
        }

        if (this.isCigentUser && user.email !== this.registerForm.value.email.toString()) {
            this.showProfileCard = false;
            this.onResendClick();
        } else {
            this.onEditUser();
        }
    }

  onVerify(user: IManageUser) {
    if (this.e.verifyCode.errors) {
      return;
    }

    const new_email = this.registerForm.value.email;
    const code = this.verifyCodeFormGroup.value.verifyCode;
       this.userSvc.checkVerificationCodeForNewEmail(new_email, code)
         .subscribe(
           () => {
             this.store.dispatch(loadUser());
             this.onEditUser();
             this.showProfileCard = true;
             this.verifyCodeFormGroup.reset();
             this.notification.showSuccess(this.fEmailUpdated);
         },
           () => {
           this.verifyCodeFormGroup.controls['verifyCode'].setErrors({invalidCode: true});
       });
  }

  onResendClick() {
     this.limitEmailsClock();
     const newEmail = this.registerForm.value.email;
     this.notification.showUpdate(this.fEmailSending);  // EMAIL_SENDING
     this.userSvc.sendVerificationToNewEmail(newEmail)
       .subscribe(() => {
           this.notification.showSuccess(this.fEmailSent);
         },
         error => {
           if (error.toLowerCase() === 'too many requests') {
             // PROFILE_TOO_MANY_REQUESTS
             this.notification.showWarning(this.fTooManyRequests);
           } else {
            this.notification.showError(this.fErrorEmailReset);
           }
       });
  }

  onEmailBlur(event, user: IManageUser) {
    this.registerForm.controls['email'].markAsTouched();
    // Call email check if email field is not in error and email has changed.
    if (user.email === this.registerForm.value.email.toString()) {
     // Email is invalid or hasn't changed, don't check email yet!
   } else {
     if (this.registerForm.controls.email.errors) {
       this.registerForm.controls['email'].setErrors({'email': true});
       return;
     }
     const email = this.registerForm.value.email;
     this.userSvc.checkUserEmailExists(email)
       .pipe(untilDestroyed(this))
       .subscribe(next => {
          if (next.exists && next.verified) {
            this.registerForm.controls.email.setErrors({'emailExists': true});
          }
        },
        () => {
          // Error state, probably email pattern
          this.registerForm.controls.email.setErrors({'pattern': true});
        }
      );
   }
 }

  openDialog(user: IManageUser): void {
    const dialogRef = this.dialog.open(GoogleAuthDialogComponent, {
      width: '650px',
      data: {
        user: user,
      },
    });

    dialogRef.afterClosed().subscribe(done => {
        if (done && user.force_two_factor) {
          this.authService.logout();
        } else {
          this.store.dispatch(loadUser());
        }
    });
  }

  limitEmailsClock() {
    this.canEmailUser = false;
    // Time should match resend_code_delay in config file
    this.myClock = window.setInterval(() => this.enableEmails(), 300000);
  }

  enableEmails() {
    this.canEmailUser = true;
    clearInterval(this.myClock);
    this.myClock = 0;
  }

  onNewRecoveryCode() {
    const dialogConfig: IPasswordAlertDialogConfig = {
      title: this.tfGenerateTitle,
      text: this.tfGenerateContent
    }
    const dialogRef = this.dialog.open<
      PasswordAlertDialogComponentComponent,
      IPasswordAlertDialogConfig,
      IPasswordAlertDialogResult>(
        PasswordAlertDialogComponentComponent,
      {
          data: dialogConfig,
          width: '400px',
        });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.userSvc.getNewRecoveryCode(result.password)
          .subscribe(response => {
            if (response.status === LoginStatus.SUCCESS) {
              this.newRecoveryCode = response.recoveryCode;
              this.generatedNewRecoveryCode = true;
            } else {
              this.notification.showError(this.translate.instant('PASSWORD_INVALID'));
            }
        });
      }
    });
  }

  onDownloadNewRecoveryCodeFile() {
    const newRecoveryCodeFile = URL.createObjectURL(new Blob([this.newRecoveryCode], {type: 'text/plain'}));
    multiDownload([newRecoveryCodeFile], {
      rename: () => 'cigent-central-console-2fa-recovery-code.txt'
    });
  }

  onDisable2FA(): void {
    const dialogConfig: IPasswordAlertDialogConfig = {
      title: this.tfDisableTitle,
      text: this.tfDisableContent
    }
    const dialogRef = this.dialog.open<
      PasswordAlertDialogComponentComponent,
      IPasswordAlertDialogConfig,
      IPasswordAlertDialogResult>(
      PasswordAlertDialogComponentComponent,
      {
        data: dialogConfig,
        width: '400px',
      });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.userSvc.disable2FA(result.password)
          .subscribe(response => {
            if (response.status === LoginStatus.SUCCESS) {
              this.store.dispatch(loadUser());

              // this.userSvc.api_get_user_info().subscribe(
              //   (user: IUser) => {
              //     this.generatedNewRecoveryCode = false;
              //     this.user = user;
              //   }
              // );
            } else {
              this.notification.showError(this.translate.instant('PASSWORD_INVALID'));
            }
          });
      }
    });
  }

  deleteAllKeys() {
    const vals = this.translate.instant(['DELETE_ALL_KEYS_TITLE', 'DELETE_ALL_KEYS_CONTENT']);
    this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title: vals['DELETE_ALL_KEYS_TITLE'],
        content: vals['DELETE_ALL_KEYS_CONTENT'],
        rejectBtnName: this.tfNo,
        acceptBtnName: this.tfYes,
        showRejectBtn: true,
      },
    }).afterClosed().subscribe(result => {
      if (result) {
        this.encryptionService.deleteAllEncryptionKeys().subscribe();
      }
    });
  }

  protected readonly UserRole = UserRole;
}
